import { useEffect, useState, useCallback, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useWallet } from "@/context/WalletContext";
import { Activity, RefreshCw, AlertTriangle, CheckCircle2, Loader2, Zap, StopCircle, ChevronDown, ChevronRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (m) => ((Number(m) || 0) / 1_000_000).toLocaleString("en-US", { maximumFractionDigits: 2 });

const TYPE_COLOR = {
  profit: "text-green-400",
  partial_return: "text-green-400",
  total_loss: "text-muted-foreground",
  failed: "text-red-400",
};

/** Admin panel: bot settlement success/failure health + throttled wave-pager retry. */
export default function SettlementHealth() {
  const { getAuthHeaders } = useWallet();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [autoRunning, setAutoRunning] = useState(false);
  const [waveSize, setWaveSize] = useState(100);
  const [progress, setProgress] = useState(null); // { cleared, waves }
  const [showExhausted, setShowExhausted] = useState(false);
  const [exhaustedList, setExhaustedList] = useState(null); // { count, items }
  const [exhLoading, setExhLoading] = useState(false);
  const stopRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/admin/settlements/health`, { headers: getAuthHeaders() });
      if (r.ok) {
        const d = await r.json();
        setData(d);
        return d;
      }
    } catch { /* noop */ } finally { setLoading(false); }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const runWave = async (dryRun, size) => {
    const r = await fetch(`${API}/api/admin/settlements/retry-failed?dry_run=${dryRun}&limit=${size}`, {
      method: "POST", headers: getAuthHeaders(),
    });
    const d = await r.json();
    if (!r.ok) {
      const msg = typeof d.detail === "string" ? d.detail : (d.detail?.error || "Retry failed");
      throw new Error(msg);
    }
    return d;
  };

  const clampWave = () => Math.min(Math.max(Number(waveSize) || 5, 5), 200);

  const preview = async () => {
    setBusy(true);
    try {
      const d = await runWave(true, clampWave());
      toast.info(`Preview: ${d.candidates} retryable, ${d.skipped_already_paid.length} already paid`);
      await load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const retryOneWave = async () => {
    const size = clampWave();
    const n = Math.min(retryable, size);
    const estSecs = Math.ceil(n / 5) * 2;
    if (!window.confirm(`Retry the next ${n} failed settlement(s) now?\n\nThis sends REAL STXOSHI transfers to each bot owner, processed in throttled batches of 5 (~${estSecs}s total) to avoid rate-limiting the Stacks node. Already-paid positions are skipped automatically.`)) {
      return;
    }
    setBusy(true);
    try {
      const d = await runWave(false, size);
      const paid = d.retried.filter((x) => x.txid).length;
      toast.success(`Wave done: ${paid} paid, ${d.skipped_already_paid.length} already paid, ${d.still_failed.length} still failing`);
      await load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const autoClear = async () => {
    const size = clampWave();
    if (!window.confirm(`Auto-clear ALL ${retryable} retryable settlement(s) in throttled waves of ${size}?\n\nThis sends REAL STXOSHI transfers and keeps running wave-after-wave until the backlog is cleared, a wave makes no progress, or the hot wallet runs short. You can hit Stop between waves.`)) {
      return;
    }
    stopRef.current = false;
    setAutoRunning(true);
    setBusy(true);
    let cleared = 0, waves = 0;
    setProgress({ cleared: 0, waves: 0 });
    try {
      while (!stopRef.current && waves < 200) {
        const d = await runWave(false, size);
        waves += 1;
        const paid = d.retried.filter((x) => x.txid).length;
        const progressed = paid + d.skipped_already_paid.length;
        cleared += progressed;
        setProgress({ cleared, waves });

        const h = await load();
        const remaining = h?.retryable_failed_count ?? 0;

        if (remaining <= 0) {
          toast.success(`Backlog cleared — ${cleared} settlement(s) resolved across ${waves} wave(s)`);
          break;
        }
        if (progressed === 0) {
          toast.warning(`Stopped after wave ${waves}: no progress (${d.still_failed.length} still failing). ${remaining} remain — check Recent failures.`);
          break;
        }
        toast.info(`Wave ${waves}: ${progressed} cleared · ${remaining} remaining…`);
        await new Promise((res) => setTimeout(res, 800)); // breather between waves
      }
      if (stopRef.current) toast.info(`Stopped by you — ${cleared} cleared across ${waves} wave(s)`);
    } catch (e) {
      toast.error(`Auto-clear stopped: ${e.message}`);
    } finally {
      setAutoRunning(false);
      setBusy(false);
      await load();
    }
  };

  const stopAuto = () => { stopRef.current = true; toast.info("Stopping after the current wave…"); };

  const loadExhausted = useCallback(async () => {
    setExhLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/settlements/exhausted?min_attempts=3&limit=200`, { headers: getAuthHeaders() });
      if (r.ok) setExhaustedList(await r.json());
    } catch { /* noop */ } finally { setExhLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleExhausted = () => {
    const next = !showExhausted;
    setShowExhausted(next);
    if (next && !exhaustedList) loadExhausted();
  };

  const shortAddr = (a) => (a ? `${a.slice(0, 6)}…${a.slice(-4)}` : "—");
  const relTime = (iso) => {
    if (!iso) return "—";
    try {
      const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
      if (s < 60) return `${Math.floor(s)}s ago`;
      if (s < 3600) return `${Math.floor(s / 60)}m ago`;
      if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
      return `${Math.floor(s / 86400)}d ago`;
    } catch { return "—"; }
  };

  if (loading) return null;
  if (!data) return null;

  const unresolved = data.unresolved_failed_count || 0;
  const retryable = data.retryable_failed_count ?? unresolved;
  const exhausted = data.exhausted_failed_count || 0;

  return (
    <Card className={`mb-4 ${unresolved > 0 ? "border-red-500/50" : "border-green-500/30"}`} data-testid="settlement-health-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Activity className="w-4 h-4 text-orange-400" />
          Settlement Health
          {unresolved > 0 ? (
            <Badge variant="destructive" className="ml-auto" data-testid="settlement-unresolved-badge">
              <AlertTriangle className="w-3 h-3 mr-1" />{unresolved} unpaid
            </Badge>
          ) : (
            <Badge className="ml-auto bg-green-600" data-testid="settlement-healthy-badge">
              <CheckCircle2 className="w-3 h-3 mr-1" />healthy
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {Object.entries(data.by_type).map(([type, v]) => (
            <div key={type} className="rounded-lg border border-border/50 bg-muted/20 p-2" data-testid={`settlement-type-${type}`}>
              <div className={`text-xs font-semibold ${TYPE_COLOR[type] || "text-foreground"}`}>{type}</div>
              <div className="font-mono text-lg font-bold">{v.count}</div>
              <div className="text-[10px] text-muted-foreground">{fmt(v.total_stxoshi)} STXOSHI</div>
            </div>
          ))}
        </div>

        {unresolved > 0 && (
          <div className="space-y-2 rounded-lg border border-orange-500/20 bg-orange-500/5 p-2.5">
            <div className="flex items-center justify-between gap-2 text-xs">
              <span className="text-muted-foreground">
                <span className="text-orange-300 font-semibold" data-testid="settlement-retryable-count">{retryable}</span> retryable
                {exhausted > 0 && (
                  <> · <button
                    onClick={toggleExhausted}
                    className="inline-flex items-center gap-0.5 text-red-300 underline decoration-dotted underline-offset-2 hover:text-red-200"
                    data-testid="settlement-exhausted-toggle"
                  >
                    {showExhausted ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                    <span data-testid="settlement-exhausted-count">{exhausted}</span> need review
                  </button></>
                )}
              </span>
              {autoRunning && progress && (
                <span className="font-mono text-[10px] text-green-300" data-testid="settlement-autoclear-progress">
                  wave {progress.waves} · {progress.cleared} cleared
                </span>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center gap-1">
                <span className="text-[10px] text-muted-foreground">Wave size</span>
                <Input
                  type="number" min={5} max={200} value={waveSize}
                  onChange={(e) => setWaveSize(e.target.value)}
                  disabled={busy}
                  className="h-7 w-16 text-xs font-mono"
                  data-testid="settlement-wave-size-input"
                />
              </div>

              <Button size="sm" variant="outline" onClick={preview} disabled={busy} data-testid="settlement-preview-btn">
                {busy && !autoRunning ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                Preview
              </Button>

              <Button size="sm" className="bg-orange-600 hover:bg-orange-700" onClick={retryOneWave} disabled={busy || retryable === 0} data-testid="settlement-retry-wave-btn">
                {busy && !autoRunning ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                Retry next {Math.min(retryable, clampWave())}
              </Button>

              {autoRunning ? (
                <Button size="sm" variant="destructive" onClick={stopAuto} data-testid="settlement-autoclear-stop-btn">
                  <StopCircle className="w-3 h-3 mr-1" />Stop
                </Button>
              ) : (
                <Button size="sm" className="bg-red-600 hover:bg-red-700" onClick={autoClear} disabled={busy || retryable === 0} data-testid="settlement-autoclear-btn">
                  <Zap className="w-3 h-3 mr-1" />Auto-clear all {retryable}
                </Button>
              )}
            </div>
          </div>
        )}

        {showExhausted && (
          <div className="rounded-lg border border-red-500/25 bg-red-500/5 p-2.5 space-y-2" data-testid="settlement-exhausted-drawer">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[10px] uppercase tracking-wider text-red-300 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Needs manual review (≥3 failed retries)
              </span>
              <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px]" onClick={loadExhausted} disabled={exhLoading} data-testid="settlement-exhausted-refresh">
                {exhLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              </Button>
            </div>

            {exhLoading && !exhaustedList ? (
              <div className="text-xs text-muted-foreground py-2">Loading…</div>
            ) : exhaustedList && exhaustedList.items.length > 0 ? (
              <div className="space-y-1 max-h-72 overflow-y-auto">
                {exhaustedList.items.map((it) => (
                  <div key={it.position_id} className="rounded border border-red-500/20 bg-background/40 p-2 text-xs space-y-1" data-testid="settlement-exhausted-row">
                    <div className="flex items-center justify-between gap-2 min-w-0">
                      <span className="font-mono truncate">{it.position_id}</span>
                      <Badge variant="destructive" className="shrink-0 text-[10px]">{it.retry_attempts}× failed</Badge>
                    </div>
                    <div className="flex items-center justify-between gap-2 text-[11px] text-muted-foreground min-w-0">
                      <span className="truncate">to {shortAddr(it.user_address)}</span>
                      <span className="font-mono text-foreground whitespace-nowrap">{fmt(it.transfer_amount)} STXOSHI</span>
                    </div>
                    <div className="text-[11px] text-red-300 break-words">
                      {it.last_retry_error || "unknown error"}
                    </div>
                    <div className="text-[10px] text-muted-foreground">last try {relTime(it.last_retry_at)}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-xs text-muted-foreground py-2" data-testid="settlement-exhausted-empty">No exhausted settlements — nothing needs manual review.</div>
            )}
          </div>
        )}

        {data.recent_failures?.length > 0 && (
          <div className="space-y-1">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Recent failures</div>
            {data.recent_failures.slice(0, 6).map((f) => (
              <div key={f.id} className="text-xs flex items-center justify-between gap-2 rounded border border-red-500/20 bg-red-500/5 p-1.5 min-w-0" data-testid="settlement-failure-row">
                <span className="font-mono truncate">{f.position_id?.slice(0, 14)}…</span>
                <span className="text-red-300 truncate flex-1 min-w-0">{f.error}</span>
                <span className="font-mono text-muted-foreground whitespace-nowrap">{fmt(f.transfer_amount)}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
